import AddButton from "components/AddButton";
import ConfirmBoxDelete from "components/ConfirmBoxDelete";
import TableFilters from "components/TableFilters";
import MaterialTableTS from "components/tables/MaterialTableTS";
import Timer from "modules/Utilities/ConfigureAgreement/Timer";
import React, { useEffect, useState } from "react";
import LeasedEquipmentStatusForm from "../form/LeasedEquipmentStatusForm";
import { PERMISSIONS } from "modules/PermissionsConstants";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import useGetStatusList from "../hooks/useGetLeasedEquipmentStatus";
import useDeleteLeasedEquipmentStatus from "../hooks/useDeleteLeasedEquipmentStatus";
import { useHistory, useLocation } from "react-router-dom";
import { ILeasedEquipStatus } from "../Types";
import {
  addStateStatus,
  editStateStatus,
  rowEditStatus,
  selectedRowsStateStatus,
  statusFetching,
  statusFilterText,
} from "../atoms/atoms";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import { notificationFilterById } from "actions/Utilities/NotificationFilter";
import EditButton from "components/EditButton";
import { toast } from "components/toastNotification";

export default function LeasedEquipmentStatusTable() {
  const { data: directData, refetch: reFetch } = useGetStatusList();

  const BulkDelete = useDeleteLeasedEquipmentStatus();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(addStateStatus);
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateStatus
  );
  const [actionEditState, setActionEditState] = useRecoilState(editStateStatus);
  const [filterTextState, setFilterTextState] =
    useRecoilState(statusFilterText);
  const setEditRowData = useSetRecoilState(rowEditStatus);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(statusFetching);

  //Local States
  const [tableData, setTableData] = useState<ILeasedEquipStatus[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<
    ILeasedEquipStatus[]
  >([]);
  const [displayNote, setDisplayNote] = useState(false);
  const [edit, setEdit] = useState(false);

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_leasedequipment_status_add;
  const editPermission =
    PERMISSIONS.permission_utilities_leasedequipment_status_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_leasedequipment_status_delete;

  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };
  // Get Data
  useEffect(() => {
    if (directData?.status == 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  // For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result: ILeasedEquipStatus[] | undefined) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setTableFilteredData(result);
          setRecent(result[0].leasedEquipmentStatusName);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent]);

  //For Filter/Search
  const filtereditems =
    tableData &&
    tableData?.filter(
      (item) =>
        item.leasedEquipmentStatusName &&
        item.leasedEquipmentStatusName
          .toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
    );
  useEffect(() => {
    if (!recent) {
      setTableFilteredData(filtereditems);
    }
  }, [filterTextState]);
  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.status)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);
    const ids = selectedRows && selectedRows.map((row) => row.id);
    BulkDelete(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
    await Timer();
    reFetch();
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Statuses" : "Status"
    }?`}</div>
  );

  const matColumns = [
    {
      title: "Status Name",
      field: "leasedEquipmentStatusName",
      customSort: (a, b) =>
        a.leasedEquipmentStatusName && b.leasedEquipmentStatusName
          ? a.leasedEquipmentStatusName
              .trim()
              .localeCompare(b.leasedEquipmentStatusName.trim())
          : a.leasedEquipmentStatusName
          ? 0
          : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "status",
      render: (rowData) => {
        return rowData?.status ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];
  return (
    <div className="bg-white p-4">
      {actionAddState || actionEditState ? (
        <div>
          <LeasedEquipmentStatusForm reFetch={reFetch} />
        </div>
      ) : (
        <>
          <div className="flex flex-row justify-between items-center">
            <TableFilters
              defaultText={filterTextState}
              recentText={recent}
              setRecent={setRecent}
              textInputName={"name"}
              columnDetails={"Status Name"}
              filterText={setFilterTextState}
              disabledButton={
                selectedRows !== undefined && selectedRows.length > 0
              }
              disabledTextBox={
                selectedRows !== undefined && selectedRows.length > 0
              }
            />

            <div className="">
              {userPermissions.includes(addPermission) ? (
                <>
                  <AddButton
                    buttonText="New Status"
                    onClick={() => handleAdd()}
                    disabled={
                      selectedRows !== undefined && selectedRows.length > 0
                    }
                  />
                </>
              ) : (
                ""
              )}
            </div>
          </div>

          <div className="">
            {userPermissions.includes(deletePermission) ? (
              <div className="text-secondary italic text-xs3 my-2">
                <p className="font-bold italic inline-block mr-1">Note:</p>
                <span>Tick the box to deactivate some data.</span>
              </div>
            ) : (
              <div className="mb-4"></div>
            )}
          </div>
          <div>
            <MaterialTableTS
              data={
                isFetching
                  ? []
                  : filterTextState === "" && recent === ""
                  ? tableData
                  : filterTextState === ""
                  ? tableFilteredData
                  : filtereditems
              }
              showTitle={true}
              columns={matColumns}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              selection={userPermissions.includes(deletePermission)}
              isLoading={isFetching}
              setConfirmModalOpen={setOpenModalDelete}
            />
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Status"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => {
              setOpenModalDelete(false);
              setSelectedRows([]);
            }}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </>
      )}
    </div>
  );
}
